import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { ArrowLeft, ShoppingBasket, Download, Truck, Loader2, Printer, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { fmtDate } from "../lib/format";
import { pdf, Document, Page, View, Text, StyleSheet, Image } from "@react-pdf/renderer";

const FACTORS = { g: 0.001, kg: 1, mg: 0.000001, ml: 0.001, l: 1, cl: 0.01, dl: 0.1, "100g": 0.1, "100ml": 0.1, pce: null };
const REF_UNIT_FOR = (u) => {
  const k = (u || "g").toLowerCase();
  if (["g", "kg", "mg", "100g"].includes(k)) return "kg";
  if (["ml", "l", "cl", "dl", "100ml"].includes(k)) return "L";
  return "pce";
};
const FORMAT_QTY = (qtyKg, ref) => {
  if (ref === "pce") return `${Math.round(qtyKg)} pce`;
  // qtyKg est exprimé en unité de référence (kg ou L)
  if (qtyKg < 1) return `${(qtyKg * 1000).toFixed(0)} ${ref === "kg" ? "g" : "mL"}`;
  return `${qtyKg.toFixed(2)} ${ref}`;
};

export default function ShoppingList() {
  const [products, setProducts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [companyName, setCompanyName] = useState("AgroManage");
  const [companyLogo, setCompanyLogo] = useState(null);
  const [selected, setSelected] = useState(new Set()); // ids des produits inclus
  const [batchPerProduct, setBatchPerProduct] = useState({}); // {productId: nbBatches}
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get("/products"),
      api.get("/suppliers").catch(() => ({ data: [] })),
      api.get("/settings").catch(() => ({ data: {} })),
    ]).then(([p, s, st]) => {
      const prods = (p.data || []).filter((x) => x.recipe?.ingredients?.length);
      setProducts(prods);
      setSuppliers(s.data || []);
      setCompanyName(st.data?.company_name || "AgroManage");
      setCompanyLogo(st.data?.logo_data_url || null);
      setSelected(new Set(prods.map((x) => x.id)));
      setBatchPerProduct(Object.fromEntries(prods.map((x) => [x.id, 1])));
    });
  }, []);

  const supplierMap = useMemo(
    () => Object.fromEntries(suppliers.map((s) => [s.id, s])),
    [suppliers]
  );

  // Agrégat des ingrédients groupés par fournisseur
  const groups = useMemo(() => {
    const map = new Map(); // supplierId|"_unassigned" -> { supplier, items: Map(name+ref → {qty, products}) }
    products.forEach((p) => {
      if (!selected.has(p.id)) return;
      const batches = Number(batchPerProduct[p.id]) || 1;
      const ings = p.recipe?.ingredients || [];
      ings.forEach((ing) => {
        const qty = Number(ing.quantity);
        if (!qty) return;
        const recipeUnit = (ing.unit || "g").toLowerCase();
        const recipeFactor = FACTORS[recipeUnit];
        const ref = REF_UNIT_FOR(recipeUnit);
        // Convertit en unité de référence (kg / L / pce)
        let qtyRef;
        if (ref === "pce") {
          qtyRef = qty * batches;
        } else {
          qtyRef = qty * (recipeFactor ?? 1) * batches;
        }
        const key = `${ing.name.trim().toLowerCase()}__${ref}`;
        const sid = ing.supplier_id || "_unassigned";
        if (!map.has(sid)) map.set(sid, new Map());
        const items = map.get(sid);
        const existing = items.get(key);
        if (existing) {
          existing.qty += qtyRef;
          existing.products.add(p.name);
          // Si la 1re occurrence n'avait pas de prix, on récupère celui d'une occurrence suivante.
          // Si plusieurs occurrences ont un prix, on garde le plus élevé (sécurité budget).
          if (ing.unit_price_chf != null) {
            const incoming = Number(ing.unit_price_chf);
            const incomingUnit = ing.price_unit || "kg";
            if (existing.unit_price_chf == null) {
              existing.unit_price_chf = incoming;
              existing.price_unit = incomingUnit;
            } else {
              // Comparer en CHF/kg (ou /L) pour rester homogène
              const existingFactor = FACTORS[(existing.price_unit || "kg").toLowerCase()] ?? 1;
              const incomingFactor = FACTORS[incomingUnit.toLowerCase()] ?? 1;
              const existingPerRef = Number(existing.unit_price_chf) / (existingFactor || 1);
              const incomingPerRef = incoming / (incomingFactor || 1);
              if (incomingPerRef > existingPerRef) {
                existing.unit_price_chf = incoming;
                existing.price_unit = incomingUnit;
              }
            }
          }
        } else {
          items.set(key, {
            name: ing.name.trim(),
            ref,
            qty: qtyRef,
            note: ing.note || "",
            unit_price_chf: ing.unit_price_chf,
            price_unit: ing.price_unit || "kg",
            products: new Set([p.name]),
          });
        }
      });
    });

    // Conversion en arrays triés
    return Array.from(map.entries())
      .map(([sid, items]) => {
        const supplier = sid === "_unassigned"
          ? { id: "_unassigned", name: "Sans fournisseur attribué" }
          : supplierMap[sid] || { id: sid, name: "Fournisseur inconnu" };
        const list = Array.from(items.values()).sort((a, b) => a.name.localeCompare(b.name, "fr"));
        // Estimation de coût par fournisseur
        let estimatedCost = 0;
        let costComplete = true;
        list.forEach((it) => {
          if (it.unit_price_chf == null) {
            costComplete = false;
            return;
          }
          // it.qty est en kg ou L ou pce ; ramène prix à la même base
          const priceFactor = FACTORS[(it.price_unit || "kg").toLowerCase()];
          if (it.ref === "pce" || priceFactor == null) {
            estimatedCost += it.qty * Number(it.unit_price_chf);
          } else {
            const pricePerRef = Number(it.unit_price_chf) / priceFactor; // CHF par kg ou L
            estimatedCost += it.qty * pricePerRef;
          }
        });
        return { supplier, items: list, estimatedCost, costComplete };
      })
      .sort((a, b) => {
        if (a.supplier.id === "_unassigned") return 1;
        if (b.supplier.id === "_unassigned") return -1;
        return a.supplier.name.localeCompare(b.supplier.name, "fr");
      });
  }, [products, selected, batchPerProduct, supplierMap]);

  const totalCost = groups.reduce((acc, g) => acc + (g.estimatedCost || 0), 0);
  const allCostComplete = groups.every((g) => g.costComplete);

  const toggleAll = (checked) => {
    setSelected(checked ? new Set(products.map((p) => p.id)) : new Set());
  };

  const onGeneratePdf = async () => {
    if (groups.length === 0) {
      toast.error("Sélectionnez au moins un produit");
      return;
    }
    setGenerating(true);
    try {
      const blob = await pdf(
        <ShoppingPdf
          groups={groups}
          companyName={companyName}
          companyLogo={companyLogo}
          generatedAt={new Date().toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" })}
          totalCost={totalCost}
          costComplete={allCostComplete}
          batchPerProduct={batchPerProduct}
          products={products.filter((p) => selected.has(p.id))}
        />
      ).toBlob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `liste-courses-${new Date().toISOString().slice(0, 10)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("PDF généré");
    } catch (e) {
      toast.error("Échec de la génération PDF");
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="space-y-6 max-w-6xl" data-testid="shopping-list-page">
      <div>
        <Link to="/parametres" className="text-xs uppercase tracking-[0.2em] font-semibold text-[#575A57] flex items-center gap-1.5 hover:text-[#1A1C1A] transition-colors">
          <ArrowLeft className="w-3.5 h-3.5" /> Paramètres
        </Link>
        <h1 className="font-heading text-2xl font-medium text-[#1A1C1A] mt-1 flex items-center gap-2">
          <ShoppingBasket className="w-6 h-6 text-[#1F4E3D]" /> Liste de courses groupée par fournisseur
        </h1>
        <p className="text-sm text-[#575A57] mt-1">
          Génère une liste cumulant les ingrédients nécessaires pour produire les recettes sélectionnées, regroupés par fournisseur.
        </p>
      </div>

      <div className="bg-white border border-[#E5E5E0] rounded-lg p-5">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <h2 className="font-heading text-base font-medium text-[#1A1C1A]">
            Produits à inclure ({selected.size}/{products.length})
          </h2>
          <div className="flex items-center gap-3">
            <button
              onClick={() => toggleAll(true)}
              className="text-xs uppercase tracking-wider text-[#575A57] hover:text-[#1A1C1A]"
              data-testid="select-all-btn"
            >Tout sélectionner</button>
            <span className="text-[#A3A6A3]">·</span>
            <button
              onClick={() => toggleAll(false)}
              className="text-xs uppercase tracking-wider text-[#575A57] hover:text-[#1A1C1A]"
              data-testid="deselect-all-btn"
            >Tout désélectionner</button>
          </div>
        </div>

        {products.length === 0 ? (
          <p className="text-sm text-[#A3A6A3] py-3">
            Aucun produit avec recette détaillée. Renseignez la fiche technique d'au moins un produit.
          </p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {products.map((p) => {
              const isSelected = selected.has(p.id);
              return (
                <div
                  key={p.id}
                  className={`border rounded-md px-3 py-2 flex items-center gap-2 ${isSelected ? "border-[#1F4E3D]/40 bg-[#F9F9F6]" : "border-[#E5E5E0] bg-white"}`}
                  data-testid={`product-toggle-${p.sku}`}
                >
                  <Checkbox
                    checked={isSelected}
                    onCheckedChange={(v) => {
                      const next = new Set(selected);
                      if (v) next.add(p.id); else next.delete(p.id);
                      setSelected(next);
                    }}
                  />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-[#1A1C1A] truncate">{p.name}</div>
                    <div className="text-[10px] uppercase tracking-wider text-[#575A57]">{p.sku}</div>
                  </div>
                  <Input
                    type="number"
                    min="1"
                    step="1"
                    value={batchPerProduct[p.id] ?? 1}
                    onChange={(e) => setBatchPerProduct({ ...batchPerProduct, [p.id]: e.target.value })}
                    className="w-14 h-8 text-center font-mono text-xs"
                    title="Nombre de productions"
                    disabled={!isSelected}
                  />
                  <span className="text-[10px] text-[#A3A6A3]">×</span>
                </div>
              );
            })}
          </div>
        )}

        <div className="mt-4 flex items-center gap-2 text-[11px] text-[#575A57]">
          <span className="bg-[#F0F0EB] px-2 py-0.5 rounded font-mono">×N</span>
          <span>Nombre de productions (1 par défaut). Augmentez pour cumuler plusieurs lots du même produit.</span>
        </div>
      </div>

      <div className="bg-white border border-[#E5E5E0] rounded-lg overflow-hidden" data-testid="grouped-preview">
        <div className="px-5 py-3 border-b border-[#E5E5E0] flex items-center justify-between flex-wrap gap-2">
          <h2 className="font-heading text-base font-medium text-[#1A1C1A] flex items-center gap-2">
            <Truck className="w-4 h-4 text-[#1F4E3D]" /> Aperçu de la liste ({groups.length} fournisseur{groups.length > 1 ? "s" : ""})
          </h2>
          <div className="flex items-center gap-3">
            {totalCost > 0 && (
              <span className="text-xs font-mono text-[#1A1C1A]">
                {allCostComplete ? "Coût estimé" : "Estimation partielle"} : <strong>{totalCost.toFixed(2)} CHF</strong>
              </span>
            )}
            <Button
              onClick={onGeneratePdf}
              disabled={generating || groups.length === 0}
              className="bg-[#1F4E3D] hover:bg-[#173B2E]"
              data-testid="generate-pdf-btn"
            >
              {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
              Télécharger PDF
            </Button>
          </div>
        </div>

        {groups.length === 0 ? (
          <div className="p-8 text-center text-[#A3A6A3] text-sm">
            Sélectionnez des produits pour voir la liste cumulée.
          </div>
        ) : (
          <div className="divide-y divide-[#F0F0EB]">
            {groups.map((g) => (
              <div key={g.supplier.id} className="p-5">
                <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                  <h3 className="font-heading text-base font-medium text-[#1A1C1A]">
                    {g.supplier.name}
                    {g.supplier.id === "_unassigned" && (
                      <span className="ml-2 text-[10px] uppercase tracking-wider bg-[#FBF4E9] border border-[#E8D3A8] text-[#8C6B2F] px-2 py-0.5 rounded-full inline-flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" /> à attribuer
                      </span>
                    )}
                  </h3>
                  <span className="text-xs font-mono text-[#575A57]">
                    {g.items.length} ingrédient{g.items.length > 1 ? "s" : ""}
                    {g.estimatedCost > 0 && <> · ~{g.estimatedCost.toFixed(2)} CHF</>}
                  </span>
                </div>
                <table className="w-full text-sm">
                  <thead className="bg-[#F9F9F6] text-[10px] uppercase tracking-wider text-[#575A57]">
                    <tr>
                      <th className="text-left px-3 py-1.5 font-semibold">Ingrédient</th>
                      <th className="text-right px-3 py-1.5 font-semibold">Quantité totale</th>
                      <th className="text-left px-3 py-1.5 font-semibold">Utilisé pour</th>
                      <th className="text-right px-3 py-1.5 font-semibold">Coût estimé</th>
                    </tr>
                  </thead>
                  <tbody>
                    {g.items.map((it, i) => {
                      const priceFactor = FACTORS[(it.price_unit || "kg").toLowerCase()];
                      let lineCost = null;
                      if (it.unit_price_chf != null) {
                        if (it.ref === "pce" || priceFactor == null) {
                          lineCost = it.qty * Number(it.unit_price_chf);
                        } else {
                          lineCost = it.qty * (Number(it.unit_price_chf) / priceFactor);
                        }
                      }
                      return (
                        <tr key={i} className="border-t border-[#F0F0EB]">
                          <td className="px-3 py-2 font-medium text-[#1A1C1A]">{it.name}</td>
                          <td className="px-3 py-2 text-right font-mono font-semibold text-[#1F4E3D]">{FORMAT_QTY(it.qty, it.ref)}</td>
                          <td className="px-3 py-2 text-xs text-[#575A57] truncate max-w-[300px]">{Array.from(it.products).join(", ")}</td>
                          <td className="px-3 py-2 text-right font-mono text-xs text-[#1A1C1A]">
                            {lineCost != null ? `${lineCost.toFixed(2)} CHF` : <span className="text-[#A3A6A3]">—</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const pdfStyles = StyleSheet.create({
  page: { padding: 32, fontFamily: "Helvetica", fontSize: 10, color: "#1A1C1A" },
  header: { borderBottom: "1pt solid #1F4E3D", paddingBottom: 8, marginBottom: 14, flexDirection: "row", justifyContent: "space-between", alignItems: "flex-end" },
  title: { fontSize: 18, fontFamily: "Helvetica-Bold", color: "#1F4E3D" },
  subtitle: { fontSize: 9, color: "#575A57", marginTop: 2 },
  meta: { fontSize: 8, color: "#575A57", textAlign: "right" },
  metaBold: { fontSize: 9, color: "#1A1C1A", fontFamily: "Helvetica-Bold" },
  totalBox: { backgroundColor: "#F9F9F6", border: "0.4pt solid #E5E5E0", borderRadius: 2, padding: 8, marginBottom: 12, flexDirection: "row", justifyContent: "space-between" },
  groupSection: { marginBottom: 14, break: false },
  groupHeader: { backgroundColor: "#1F4E3D", padding: "4 6", marginBottom: 0 },
  groupName: { color: "#fff", fontFamily: "Helvetica-Bold", fontSize: 11 },
  groupMeta: { color: "#A4C9A0", fontSize: 8, marginTop: 1 },
  thead: { flexDirection: "row", backgroundColor: "#F0F0EB", padding: "3 6", borderBottom: "0.4pt solid #E5E5E0" },
  th: { fontSize: 7, fontFamily: "Helvetica-Bold", color: "#575A57", textTransform: "uppercase", letterSpacing: 0.4 },
  row: { flexDirection: "row", padding: "4 6", borderBottom: "0.3pt solid #F0F0EB" },
  rowAlt: { backgroundColor: "#F9F9F6" },
  td: { fontSize: 9, color: "#1A1C1A" },
  tdRight: { fontSize: 9, color: "#1A1C1A", textAlign: "right", fontFamily: "Helvetica-Bold" },
  tdSmall: { fontSize: 7, color: "#575A57" },
  productsList: { fontSize: 7, color: "#575A57", fontFamily: "Helvetica-Oblique", marginTop: 1 },
  unassignedNote: { backgroundColor: "#FBF4E9", border: "0.4pt solid #E8D3A8", padding: 6, marginTop: 4, fontSize: 8, color: "#8C6B2F" },
  footer: { position: "absolute", bottom: 20, left: 32, right: 32, fontSize: 7, color: "#A3A6A3", textAlign: "center", borderTop: "0.3pt solid #E5E5E0", paddingTop: 4 },
  logo: { width: 50, height: 50, objectFit: "contain", marginRight: 10 },
  signLine: { marginTop: 28, fontSize: 8, color: "#575A57" },
});

function ShoppingPdf({ groups, companyName, companyLogo, generatedAt, totalCost, costComplete, batchPerProduct, products }) {
  return (
    <Document>
      <Page size="A4" style={pdfStyles.page}>
        <View style={pdfStyles.header} fixed>
          <View style={{ flexDirection: "row", alignItems: "center" }}>
            {companyLogo && <Image style={pdfStyles.logo} src={companyLogo} />}
            <View>
              <Text style={pdfStyles.title}>Liste de courses</Text>
              <Text style={pdfStyles.subtitle}>{companyName} — Achats matières</Text>
            </View>
          </View>
          <View>
            <Text style={pdfStyles.meta}>Généré le</Text>
            <Text style={pdfStyles.metaBold}>{generatedAt}</Text>
          </View>
        </View>

        <View style={pdfStyles.totalBox}>
          <View>
            <Text style={pdfStyles.tdSmall}>Productions planifiées</Text>
            <Text style={[pdfStyles.td, { fontFamily: "Helvetica-Bold", marginTop: 2 }]}>
              {products.map((p) => `${batchPerProduct[p.id] || 1}× ${p.name}`).join(" · ")}
            </Text>
          </View>
          <View style={{ alignItems: "flex-end" }}>
            <Text style={pdfStyles.tdSmall}>{costComplete ? "Coût total estimé" : "Estimation partielle"}</Text>
            <Text style={{ fontSize: 14, fontFamily: "Helvetica-Bold", color: "#1F4E3D", marginTop: 1 }}>
              {totalCost > 0 ? `${totalCost.toFixed(2)} CHF` : "—"}
            </Text>
          </View>
        </View>

        {groups.map((g) => (
          <View key={g.supplier.id} style={pdfStyles.groupSection} wrap={false}>
            <View style={pdfStyles.groupHeader}>
              <Text style={pdfStyles.groupName}>
                {g.supplier.name}
                {g.estimatedCost > 0 ? `  ·  ~${g.estimatedCost.toFixed(2)} CHF` : ""}
              </Text>
              <Text style={pdfStyles.groupMeta}>
                {g.items.length} ingrédient{g.items.length > 1 ? "s" : ""}
              </Text>
            </View>
            <View style={pdfStyles.thead}>
              <Text style={[pdfStyles.th, { flex: 4 }]}>Ingrédient</Text>
              <Text style={[pdfStyles.th, { flex: 2, textAlign: "right" }]}>Quantité</Text>
              <Text style={[pdfStyles.th, { flex: 2, textAlign: "right" }]}>Coût</Text>
            </View>
            {g.items.map((it, i) => {
              const factors = { g: 0.001, kg: 1, mg: 0.000001, ml: 0.001, l: 1, cl: 0.01, dl: 0.1, "100g": 0.1, "100ml": 0.1, pce: null };
              const priceFactor = factors[(it.price_unit || "kg").toLowerCase()];
              let lineCost = null;
              if (it.unit_price_chf != null) {
                if (it.ref === "pce" || priceFactor == null) {
                  lineCost = it.qty * Number(it.unit_price_chf);
                } else {
                  lineCost = it.qty * (Number(it.unit_price_chf) / priceFactor);
                }
              }
              const formatted = it.ref === "pce"
                ? `${Math.round(it.qty)} pce`
                : it.qty < 1
                  ? `${(it.qty * 1000).toFixed(0)} ${it.ref === "kg" ? "g" : "mL"}`
                  : `${it.qty.toFixed(2)} ${it.ref}`;
              return (
                <View key={i} style={[pdfStyles.row, i % 2 === 1 ? pdfStyles.rowAlt : {}]}>
                  <View style={{ flex: 4 }}>
                    <Text style={pdfStyles.td}>{it.name}</Text>
                    <Text style={pdfStyles.productsList}>Pour : {Array.from(it.products).join(", ")}</Text>
                  </View>
                  <Text style={[pdfStyles.tdRight, { flex: 2 }]}>{formatted}</Text>
                  <Text style={[pdfStyles.tdRight, { flex: 2, color: lineCost != null ? "#1F4E3D" : "#A3A6A3" }]}>
                    {lineCost != null ? `${lineCost.toFixed(2)} CHF` : "—"}
                  </Text>
                </View>
              );
            })}
            {g.supplier.id === "_unassigned" && (
              <View style={pdfStyles.unassignedNote}>
                <Text>Astuce : assignez un fournisseur à ces ingrédients dans la fiche technique pour qu'ils apparaissent dans le bon groupe la prochaine fois.</Text>
              </View>
            )}
            <Text style={pdfStyles.signLine}>Signature : ____________________________</Text>
          </View>
        ))}

        <Text style={pdfStyles.footer} fixed>
          {companyName} — Liste de courses générée automatiquement par AgroManage le {generatedAt}
        </Text>
      </Page>
    </Document>
  );
}
